import { cfetch } from '@confluence/network';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { isLocal } from '@confluence/ssr-utilities/entry-points/env.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { LocalStorageQuery } from './LocalStorageQueryRaw.ts';
import { PERSISTED_KEYS_ON_SERVER } from './LocalStorageKeys.ts';
import { getKeysForLocalStorageQuery } from './storageHelpers.ts';
import type { LocalStorageQueryVariables } from './__types__/LocalStorageQuery.ts';
import type { StorageManagerInitContext } from './StorageManagerInitContext.ts';

type RemoteLocalStorageStringValue = {
	key: string;
	value: string;
};

const LOCAL_STORAGE_ITEM_LENGTH_METRIC = 'localStorageMaxItemValueLength';
const LOCAL_STORAGE_TYPE = 'localStorage';
const persistedRemoteStorageKeyEntries = Object.entries(PERSISTED_KEYS_ON_SERVER);
const scheduledRemoteLocalStorageValues = new WeakSet<unknown[]>();

function scheduleRemoteLocalStorageMaxItemLengthMetric(values: unknown) {
	if (
		!Array.isArray(values) ||
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR) ||
		scheduledRemoteLocalStorageValues.has(values) ||
		typeof window.requestIdleCallback !== 'function'
	) {
		return;
	}

	scheduledRemoteLocalStorageValues.add(values);
	// eslint-disable-next-line compat/compat -- Guarded above; unsupported browsers skip this diagnostic.
	window.requestIdleCallback(() => submitRemoteLocalStorageMaxItemLengthMetric(values));
}

function submitRemoteLocalStorageMaxItemLengthMetric(values: unknown) {
	if (
		!Array.isArray(values) ||
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR) || // skipping in SSR because getMonitoringClient().gauge is a no-op in SSR
		!expValEquals('confluence_frontend_localstorage_item_length', 'isEnabled', true)
	) {
		return;
	}

	const longestItem = values.reduce<{
		storageKeyTag: string;
		valueLength: number;
	} | null>((currentLongestItem, entry: unknown) => {
		if (!isRemoteLocalStorageStringValue(entry)) {
			return currentLongestItem;
		}

		const storageKeyTag = getPersistedRemoteStorageKeyTag(entry.key);
		if (!storageKeyTag) {
			return currentLongestItem;
		}

		const valueLength = entry.value.length;
		if (!currentLongestItem || valueLength > currentLongestItem.valueLength) {
			return { storageKeyTag, valueLength };
		}

		return currentLongestItem;
	}, null);

	if (!longestItem) {
		return;
	}

	getMonitoringClient().gauge(LOCAL_STORAGE_ITEM_LENGTH_METRIC, longestItem.valueLength, [
		`storageKey:${longestItem.storageKeyTag}`,
		'source:remote_fetch',
		`storageType:${LOCAL_STORAGE_TYPE}`,
	]);
}

function isRemoteLocalStorageStringValue(entry: unknown): entry is RemoteLocalStorageStringValue {
	if (!entry || typeof entry !== 'object') {
		return false;
	}

	const { key, value } = entry as Record<string, unknown>;
	return typeof key === 'string' && typeof value === 'string';
}

function getPersistedRemoteStorageKeyTag(remoteKey: string): string | null {
	const persistedStorageKeyEntry = persistedRemoteStorageKeyEntries.find(([, storageKey]) =>
		remoteKey.endsWith(`.${storageKey}`),
	);

	return persistedStorageKeyEntry?.[0] ?? null;
}

export async function getRemoteLocalStorage({
	userId,
	isLicensed,
	regionSteeringHint,
}: StorageManagerInitContext): Promise<any> {
	if (window['__REMOTE_LOCAL_STORAGE__']) {
		// This value can be returned from SSR result, so measure it when the client first accesses it.
		// Keep this diagnostic off the critical path and schedule it at most once per payload.
		scheduleRemoteLocalStorageMaxItemLengthMetric(window['__REMOTE_LOCAL_STORAGE__']);
		return window['__REMOTE_LOCAL_STORAGE__'];
	}

	const tenantHostname =
		typeof window !== 'undefined' ? window.location?.hostname : getSSRRenderInputs().host;

	const regionHint =
		isGCPtenant(tenantHostname) || fg('cc-agg-migration-use-agg-endpoint')
			? regionSteeringHint || window['GLOBAL_AGG_REGION_STEERING_HINT']
			: undefined;

	const URI =
		isGCPtenant(tenantHostname) || fg('cc-agg-migration-use-agg-endpoint')
			? '/gateway/api/graphql/slow'
			: window['GLOBAL_APOLLO_CLIENT_URI'] || '/cgraphql';
	return (window['__REMOTE_LOCAL_STORAGE__'] = SSRMeasures.run(
		'ssr-app/prepare/preloadQuery/fetch:LocalStorageQuery',
		async () => {
			let result = null;
			try {
				if (isLicensed && userId) {
					const variables: LocalStorageQueryVariables = {
						stringKeys: getKeysForLocalStorageQuery(userId),
					};

					const specialHeaders: Record<string, string> = {};
					if (isGCPtenant(tenantHostname) || fg('cc-agg-migration-use-agg-endpoint')) {
						specialHeaders['atl-attribution'] = JSON.stringify({
							product: 'Confluence Cloud',
							service: 'confluence-frontend',
						});
						if (regionHint) {
							specialHeaders['Atl-Edge-Region-Hint'] = regionHint;
						}
					} else if (
						// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
						Boolean(process.env.REACT_SSR) &&
						Boolean(process.env.BIFROST_TEMPLATE_BUILD) &&
						!isLocal()
					) {
						specialHeaders['x-ssr-asap-audience'] = 'cc-graphql';
					}

					const res = await cfetch(`${URI}?q=LocalStorageQuery`, {
						method: 'POST',
						headers: {
							'X-APOLLO-OPERATION-NAME': 'LocalStorageQuery',
							'Content-Type': 'application/json',
							...specialHeaders,
						},
						body: JSON.stringify({
							operationName: 'LocalStorageQuery',
							query: LocalStorageQuery,
							variables,
						}),
					});
					if (res.ok) {
						const { data } = await res.json();
						result = data?.localStorage?.stringValues;
					}
				}
			} catch {}
			return (window['__REMOTE_LOCAL_STORAGE__'] = result);
		},
	));
}

/**
 * Checks if the current domain is a GCP tenant matching the pattern: .*-cdp-{cdp-id}\.jira-dev\.com
 * @param {string | undefined} hostname optional hostname to evaluate
 * @returns {boolean} true if the domain matches the GCP tenant pattern
 */
function isGCPtenant(hostname?: string): boolean {
	if (!hostname) {
		return false;
	}

	const gcpTenantPattern = /^.*-cdp-\w+\.jira-dev\.com$/;
	return gcpTenantPattern.test(hostname);
}
